import { RefreshCw, Wrench } from "@ews-admin/global-design-system";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Logo } from "../../components/atoms/Logo/Logo";
import { getTextDirection } from "../../hooks/useDocumentLanguage";
import {
  selectMaintenanceFlag,
  setMaintenanceState,
} from "../../store/reducers/appNoticeSlice";
import { useAppDispatch } from "../../store/store";
import { envConfig } from "../../utils/config-utils";
import { resolveMaintenance } from "../../webclients/appNotice.client";

const POLL_INTERVAL_MS = 60_000;

/**
 * Full-screen maintenance page.
 *
 * - Renders no nav header, no login form — fully isolated.
 * - Polls `GET /public/app-config/resolve?feature_type=MAINTENANCE` every 60s; when
 *   the BFF reports `enabled === false`, the user is redirected to `/`.
 * - Mount guard: if maintenance is not currently active, immediately redirect to `/`.
 *   This guard relies on `useAppNotices()` having already populated `loaded === true`
 *   in the root of the app; the App routing layer additionally prevents reaching this
 *   page when not in maintenance, so this is defense-in-depth.
 * - RTL: when the active locale is `ar`, the root container is rendered with `dir="rtl"`.
 *   The document-level `lang`/`dir` are handled once by `useDocumentLanguage`.
 */
export const MaintenancePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const maintenance = useSelector(selectMaintenanceFlag);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const direction = getTextDirection(i18n.language);

  // Mount guard: only redirect once we know the flag state. If maintenance is not
  // active, send the user back to the home page.
  useEffect(() => {
    if (maintenance.loaded && !maintenance.enabled) {
      navigate("/", { replace: true });
    }
  }, [maintenance.loaded, maintenance.enabled, navigate]);

  // Poll the BFF every 60s; when maintenance is lifted, navigate to "/".
  useEffect(() => {
    const environment = envConfig.env;

    intervalRef.current = setInterval(() => {
      resolveMaintenance(environment)
        .then(response => {
          const stillActive = response?.data?.enabled === true;
          dispatch(setMaintenanceState(stillActive));
          if (!stillActive) {
            navigate("/", { replace: true });
          }
          return stillActive;
        })
        .catch(() => {
          // Fail-open: keep the page visible until the next tick succeeds.
          return null;
        });
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [dispatch, navigate]);

  return (
    <div
      dir={direction}
      className="flex min-h-screen flex-col items-center justify-between bg-gradient-to-br from-ews-primary to-ews-secondary px-4 py-8 text-center"
      data-testid="maintenance-page"
    >
      <header className="w-full max-w-7xl">
        <div className="flex items-center justify-center">
          <Logo white />
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center">
        <div
          className="mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-white/10 text-white"
          aria-hidden="true"
        >
          <Wrench className="h-12 w-12" />
        </div>

        <h1 className="mb-4 text-3xl font-bold text-white md:text-5xl">
          {t("maintenance.title")}
        </h1>

        <p className="mx-auto mb-6 max-w-2xl text-lg text-gray-100">
          {t("maintenance.description")}
        </p>

        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-gray-100">
          <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>{t("maintenance.retry_message")}</span>
        </div>
      </main>

      <footer className="text-sm text-gray-200">
        {t("maintenance.footer", { year: new Date().getFullYear() })}
      </footer>
    </div>
  );
};

export default MaintenancePage;
